import {
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
  Modal,
} from "antd";
import { useEffect, useState } from "react";
import {
  ReloadOutlined,
  FilterOutlined,
  UserSwitchOutlined,
  EditOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import MotionHoc from "../../../utils/MotionHoc";
import {
  HEADERS_EXPORT_BEN,
  POST_CHANNGE_JOBS_ENFORCE,
  POST_MAIN_ENFORCEMENT,
} from "../../API/apiUrls";
import useLawyerNamesNew from "../../../hook/UseLawyerNameNew";
import axios from "axios";

const Main = () => {
  const { nameLawyers, loadingUseLawyerNames } = useLawyerNamesNew({
    auto: true,
    toast: false,
  });
  const userId = parseInt(localStorage.getItem("USER_ID"));

  const [arrayTable, setArrayTable] = useState([]);
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  // ✅ track แยกต่อแถว: { [idQueue]: newLawyerValue }
  const [editingMap, setEditingMap] = useState({}); // idQueue → true/false
  const [changeMap, setChangeMap] = useState({}); // idQueue → userId ใหม่
  const [memoModal, setMemoModal] = useState({
    open: false,
    record: null,
    memo: "",
  });

  // ── Lawyer options ────────────────────────────────────────
  const lawyerOptions = (nameLawyers ?? [])
    .filter(
      (l) =>
        l.ACTIVE_STATUS === 1 &&
        l.COMPANY_ID !== 3 &&
        (l.ROLE_ID === 3 || l.ROLE_ID === 4),
    )
    .map((l) => ({ label: l.NNAME ?? l.NAME1, value: l.id ?? l.userId }));

  // ── Load ──────────────────────────────────────────────────
  const loadMainData = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_MAIN_ENFORCEMENT,
        { userId: 1 },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res.status === 200) {
        if (res.data.success === "ไม่พบข้อมูล") {
          message.warning("ไม่พบข้อมูล");
          return;
        }
        const newData = (res.data?.data ?? []).map((item, i) => ({
          ...item,
          key: i + 1,
        }));
        setArrayTable(newData);
        setDataArr(newData);
        setEditingMap({});
        setChangeMap({});
      } else {
        message.error("ไม่มีข้อมูล");
        setArrayTable([]);
        setDataArr([]);
      }
    } catch (err) {
      console.error(err);
      message.error(`ไม่พบข้อมูล: ${err.message}`);
      setArrayTable([]);
      setDataArr([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMainData();
  }, []);

  // ── Filter ────────────────────────────────────────────────
  const handleFilterData = () => {
    setLoading(true);
    const q = searchInputTable.trim().toLowerCase();
    const result = (dataArr ?? []).filter(
      (item) =>
        !q ||
        [item.CUSCOD, item.newContno, item.NAME1, item.NAME2]
          .map((v) => String(v ?? "").toLowerCase())
          .some((val) => val.includes(q)),
    );
    setArrayTable(result);
    setTimeout(() => setLoading(false), 250);
    result.length === 0
      ? message.error("ไม่พบข้อมูล")
      : message.success(`พบข้อมูล ${result.length} รายการ`);
  };

  // ── Post ──────────────────────────────────────────────────
  const postStatus = async (record, memo) => {
    const newLawyer = changeMap[record.idQueue];
    if (!newLawyer) {
      message.warning("กรุณาเลือกทนายใหม่ก่อน");
      return;
    }

    const payload = {
      userId,
      IdQueue: record.idQueue,
      ReceiveByOld: record.receiveBy, //ข้อมูลที่ยังไม่ถูกแก้ไข
      ReceiveByNew: newLawyer, //ผู้รับผิดชอบใหม่
      Memo: memo,
    };
    try {
      const res = await axios.post(POST_CHANNGE_JOBS_ENFORCE, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res.status === 200) {
        message.success("เปลี่ยนทำเร็จ");
        loadMainData();
      }
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        err.message;
      const errorMap = {
        400: `ข้อมูลไม่ถูกต้อง: ${serverMsg}`,
        404: "ไม่พบปลายทาง (404)",
        500: "เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)",
      };
      message.error(
        errorMap[status] ??
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
      );
    }
  };

  // ── Memo modal ────────────────────────────────────────────
  const openMemoModal = (record) => {
    const newLawyer = changeMap[record.idQueue];
    if (!newLawyer) {
      message.warning("กรุณาเลือกทนายใหม่ก่อน");
      return;
    }
    setMemoModal({ open: true, record, memo: "" });
  };

  const closeMemoModal = () => {
    setMemoModal({ open: false, record: null, memo: "" });
  };

  const handleConfirmMemoModal = async () => {
    if (!memoModal.memo.trim()) {
      message.warning("กรุณากรอกหมายเหตุก่อนยืนยัน");
      return;
    }
    await postStatus(memoModal.record, memoModal.memo.trim());
    closeMemoModal();
  };

  // ── Columns ───────────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      align: "center",
      width: "10%",
      render: (_, __, idx) => idx + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "newContno",
      align: "newContno",
      width: "15%",
    },
    {
      title: "ชื่อ-นามสกุล",
      align: "left",
      width: "20%",
      render: (_, r) =>
        `${r.SNAM ?? ""}${r.NAME1 ?? ""} ${r.NAME2 ?? ""}`.trim(),
    },
    {
      title: "สถานะ",
      align: "center",
      width: "15%",
      render: (_, r) => (
        <Tag color="blue">
          {r.sendDate && r.receiveDate ? "รับงานแล้ว" : "ยังไม่รับงาน"}
        </Tag>
      ),
    },
    {
      title: "ผู้รับผิดชอบ",
      align: "center",
      width: "20%",
      render: (_, r) => {
        const isEditing = !!editingMap[r.idQueue];
        return (
          <Select
            value={changeMap[r.idQueue] ?? r.receiveBy}
            style={{ width: 150 }}
            loading={loadingUseLawyerNames}
            disabled={!isEditing} // ✅ disable ถ้าแถวนี้ไม่ได้แก้ไข
            options={lawyerOptions}
            onChange={(val) =>
              setChangeMap((prev) => ({ ...prev, [r.idQueue]: val }))
            }
          />
        );
      },
    },
    {
      title: "การจัดการ",
      align: "center",
      width: "20%",
      render: (_, r) => {
        const isEditing = !!editingMap[r.idQueue];
        return isEditing ? (
          <Space>
            {/* ✅ เปิด modal กรอกหมายเหตุก่อนยืนยัน */}
            <Button
              type="primary"
              size="small"
              icon={<UserSwitchOutlined />}
              onClick={() => openMemoModal(r)}
            >
              เปลี่ยนผู้รับผิดชอบ
            </Button>
            {/* ✅ ยกเลิก reset เฉพาะแถวนี้ */}
            <Button
              danger
              size="small"
              icon={<CloseOutlined />}
              onClick={() => {
                setEditingMap((prev) => ({ ...prev, [r.idQueue]: false }));
                setChangeMap((prev) => {
                  const n = { ...prev };
                  delete n[r.idQueue];
                  return n;
                });
              }}
            />
          </Space>
        ) : (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() =>
              setEditingMap((prev) => ({ ...prev, [r.idQueue]: true }))
            }
          >
            แก้ไข
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <Card style={{ marginBottom: 12 }}>
        <Space>
          <Input
            placeholder="ค้นหาสัญญา / ชื่อ / เลขบัตร"
            value={searchInputTable}
            onChange={(e) => setSearchInputTable(e.target.value)}
            onPressEnter={handleFilterData}
            style={{ width: 260, borderRadius: 8 }}
            allowClear
          />
          <Button
            icon={<FilterOutlined />}
            onClick={handleFilterData}
            disabled={dataArr.length === 0}
            style={{
              borderRadius: 8,
              backgroundColor: "#fff7d6",
              border: "1px solid #ffe58f",
            }}
          >
            กรองข้อมูล
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSearchInputTable("");
              setArrayTable(dataArr);
            }}
            disabled={dataArr.length === 0}
          >
            ล้าง
          </Button>
          <Button
            icon={<ReloadOutlined />}
            type="primary"
            ghost
            onClick={loadMainData}
            loading={loading}
          >
            โหลดใหม่
          </Button>
        </Space>
      </Card>

      <Card>
        <Spin spinning={loading} size="large" tip="Loading...">
          <Table
            size="small"
            columns={columns}
            dataSource={arrayTable}
            rowKey={(r) => r.idQueue ?? r.key}
            scroll={{ x: "max-content" }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
            }}
            footer={() => (
              <span>จำนวนสัญญาทั้งหมด {arrayTable.length} รายการ</span>
            )}
          />
        </Spin>
      </Card>

      <Modal
        title="ยืนยันการเปลี่ยนผู้รับผิดชอบ"
        open={memoModal.open}
        onOk={handleConfirmMemoModal}
        onCancel={closeMemoModal}
        okText="อนุมัติ"
        cancelText="ยกเลิก"
      >
        <p>เปลี่ยนจาก {memoModal.record?.receiveByName} → ผู้รับผิดชอบใหม่</p>
        <Input.TextArea
          rows={4}
          placeholder="กรอกหมายเหตุ / memo"
          value={memoModal.memo}
          onChange={(e) =>
            setMemoModal((prev) => ({ ...prev, memo: e.target.value }))
          }
        />
      </Modal>
    </>
  );
};

const MainChangeInvestor = MotionHoc(Main);
export default MainChangeInvestor;
